import { LitElement, TemplateResult, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { AdoptedStyleSheets } from '../../../controllers/adopted-stylesheets';
import innerStyles from './styles/outline-teaser-card.base.pcss?inline';
import globalStyles from './styles/outline-teaser-card.global.pcss?inline';
import { SlotHelper } from '../../../controllers/slot-helper';

export const cardAccentVariants = ['primary', 'secondary', 'tertiary'] as const;
export type CardAccentVariant = (typeof cardAccentVariants)[number];


/**
 * The OutlineTeaserCard component
 * @element outline-teaser-card
 */
@customElement('outline-teaser-card')
export class OutlineTeaserCard extends LitElement {
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: innerStyles,
  });
  private _slotHelper = new SlotHelper(this);

  @property({ type: String, reflect: true })
  accent: CardAccentVariant;

  @property({ type: Boolean, attribute: 'has-eyebrow' })
  hasEyebrow: boolean;

  @property({ type: Boolean, attribute: 'has-date' })
  hasDate: boolean;

  @property({ type: Boolean, attribute: 'has-description' })
  hasDescription: boolean;

  @property({ type: Boolean, attribute: 'has-image', reflect: true })
  hasImage: boolean;

  @property({ type: Boolean, attribute: 'has-line-1' })
  hasLine1: boolean;

  @property({ type: Boolean, attribute: 'has-line-2' })
  hasLine2: boolean;

  @property({ type: Boolean, attribute: 'has-line-3' })
  hasLine3: boolean;

  @property({ type: Boolean, attribute: 'has-phone' })
  hasPhone: boolean;

  @property({ type: Boolean, attribute: 'has-cta' })
  hasCTA: boolean;

  @property({ type: Boolean, attribute: 'has-hours' })
  hasHours: boolean;

  @property({ type: Boolean, attribute: 'is-location' })
  isLocation: boolean;

  @property({ type: Boolean, attribute: 'without-border' })
  withoutBorder: boolean;

  @property({ type: Boolean, attribute: 'is-in-view-row', reflect: true })
  isInViewRow: boolean;

  render(): TemplateResult {
    this.hasEyebrow = this._slotHelper.isNotEmpty('eyebrow');
    this.hasDate = this._slotHelper.isNotEmpty('date');
    this.hasDescription = this._slotHelper.isNotEmpty('description');
    this.hasImage = this._slotHelper.isNotEmpty('image');
    this.hasLine1 = this._slotHelper.isNotEmpty('line1');
    this.hasLine2 = this._slotHelper.isNotEmpty('line2');
    this.hasLine3 = this._slotHelper.isNotEmpty('line3');
    this.hasPhone = this._slotHelper.isNotEmpty('phone');
    this.hasHours = this._slotHelper.isNotEmpty('hours');
    this.hasCTA = this._slotHelper.isNotEmpty('cta');
    this.isLocation = this.isLocationTeaser();

    return html`
      <section
        class="outline-teaser-card${this.withoutBorder
          ? ' without-border'
          : ''}"
      >
        <div class="outline-teaser-card--main">
          ${this.eyebrowTemplate()}
          <div class="heading-wrapper">
            <slot name="heading"></slot>
          </div>
          ${this.dateTemplate()} ${this.descriptionTemplate()}
          ${this.isLocation
            ? html` <div class="outline-teaser-card--location-info">
                <div class="outline-teaser-card--location-col">
                  ${this.addressTemplate()} ${this.ctaTemplate()}
                </div>
                <div class="outline-teaser-card--location-col">
                  ${this.phoneTemplate()} ${this.hoursTemplate()}
                </div>
              </div>`
            : null}
        </div>
        ${this.imageTemplate()}
      </section>
    `;
  }

  isLocationTeaser() {
    return this.hasLine1 || this.hasPhone || this.hasCTA || this.hasHours;
  }

  eyebrowTemplate() {
    if (this.hasEyebrow) {
      return html`<div class="eyebrow-wrapper">
        <slot name="eyebrow"></slot>
      </div>`;
    } else return;
  }

  dateTemplate() {
    if (this.hasDate) {
      return html`<div class="date-wrapper"><slot name="date"></slot></div>`;
    } else return;
  }

  hoursTemplate() {
    if (this.hasHours) {
      return html`<div class="hours-wrapper"><slot name="hours"></slot></div>`;
    } else return;
  }

  descriptionTemplate() {
    if (this.hasDescription) {
      return html`<div class="description-wrapper">
        <slot name="description"></slot>
      </div>`;
    } else return;
  }

  addressTemplate() {
    if (this.hasLine1 || this.hasLine2 || this.hasLine3) {
      return html`
        <div class="address-wrapper">
          ${this.hasLine1
            ? html`<div class="line-1-wrapper"><slot name="line1"></slot></div>`
            : null}
          ${this.hasLine2
            ? html`<div class="line-2-wrapper"><slot name="line2"></slot></div>`
            : null}
          ${this.hasLine3
            ? html`<div class="line-3-wrapper"><slot name="line3"></slot></div>`
            : null}
        </div>
      `;
    } else return;
  }

  phoneTemplate() {
    if (this.hasPhone) {
      return html`<div class="phone-wrapper"><slot name="phone"></slot></div>`;
    } else return;
  }

  imageTemplate() {
    if (this.hasImage) {
      return html` <div class="outline-teaser-card--image">
        <div class="image-wrapper">
          <slot name="image"></slot>
        </div>
      </div>`;
    } else return;
  }

  ctaTemplate() {
    if (this.hasCTA) {
      return html`<div class="cta-wrapper"><slot name="cta"></slot></div>`;
    } else return;
  }
}
declare global {
  interface HTMLElementTagNameMap {
    'outline-teaser-card': OutlineTeaserCard;
  }
}
